import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AcademicCapIcon,
  BookOpenIcon,
  ChatBubbleLeftRightIcon,
  CheckCircleIcon,
  ClipboardDocumentCheckIcon,
  ClockIcon,
  CreditCardIcon,
  EnvelopeIcon,
  InboxIcon,
  UserCircleIcon,
  WrenchScrewdriverIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import {
  AppPage, BTN_PRIMARY, BTN_SECONDARY, CARD, Empty, Field, HeroPanel, INPUT, Pill, Segmented,
} from '../components/UI/AppUI';

const API_URL = process.env.REACT_APP_API_URL || 'https://api.air-brief.com';
const SUPPORT_EMAIL = 'support@air-brief.com';

const CATEGORIES = [
  { value: 'general', label: 'General', icon: ChatBubbleLeftRightIcon },
  { value: 'billing', label: 'Billing & payments', icon: CreditCardIcon },
  { value: 'account', label: 'Account', icon: UserCircleIcon },
  { value: 'course', label: 'Course content', icon: BookOpenIcon },
  { value: 'exam', label: 'Exams', icon: ClipboardDocumentCheckIcon },
  { value: 'technical', label: 'Technical issue', icon: WrenchScrewdriverIcon },
];
const CATEGORY_LABEL = Object.fromEntries(CATEGORIES.map((c) => [c.value, c.label]));
const STATUS = {
  open: { label: 'Open', tone: 'blue' },
  in_progress: { label: 'In progress', tone: 'amber' },
  resolved: { label: 'Resolved', tone: 'green' },
  closed: { label: 'Closed', tone: 'gray' },
};
const PRIORITIES = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'urgent', label: 'Urgent' },
];
const FAQS = [
  { q: 'I paid by CliQ. When do I get access?', a: 'We check transfers within 12 hours and unlock your plan as soon as it is confirmed. You can follow the request under Settings → Payments.' },
  { q: "I can't see my course", a: 'Make sure your plan is active under Settings → Purchases. If it shows as active and the course is still missing, sign out and back in, then send us a ticket.' },
  { q: 'An exam will not load', a: 'Refresh the page or try another browser. If it keeps happening, tell us the subject and what you were doing.' },
  { q: 'I found a wrong answer', a: 'Send a request under Course content with the subject and the question text, and we will check it.' },
];

const blankForm = (user, phone = '') => ({
  subject: '', category: 'general', priority: 'medium', description: '',
  email: user?.email || '', phone_number: phone || user?.phone_number || '',
});

const Support2 = () => {
  const { user } = useAuth();
  const [form, setForm] = useState(() => blankForm(user));
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');
  const [tickets, setTickets] = useState([]);
  const [loadingTickets, setLoadingTickets] = useState(true);
  const [openFaq, setOpenFaq] = useState(0);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target ? e.target.value : e }));

  useEffect(() => {
    if (user) setForm((f) => ({ ...f, email: f.email || user.email || '', phone_number: f.phone_number || user.phone_number || '' }));
  }, [user]);

  const fetchTickets = async () => {
    try {
      const r = await fetch(`${API_URL}/api/support/tickets/`, { headers: { Authorization: `Bearer ${localStorage.getItem('atpl_access_token')}` } });
      if (r.ok) {
        const d = await r.json();
        setTickets(d.results || d);
      }
    } catch {
      // The list is optional; the form still works
    } finally {
      setLoadingTickets(false);
    }
  };
  useEffect(() => { fetchTickets(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    setError('');
    try {
      const r = await fetch(`${API_URL}/api/support/tickets/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('atpl_access_token')}` },
        body: JSON.stringify(form),
      });
      if (r.ok) {
        setSent(true);
        setForm(blankForm(user, form.phone_number));
        fetchTickets();
      } else if (r.status === 429) {
        setError('You have sent several requests in a short time. Please wait a little and try again.');
      } else {
        const d = await r.json().catch(() => ({}));
        const first = Object.values(d)[0];
        setError(Array.isArray(first) ? first[0] : (d.detail || 'Your request could not be sent. Please check the form and try again.'));
      }
    } catch {
      setError('Your request could not be sent. Check your connection and try again.');
    } finally {
      setSending(false);
    }
  };

  const openCount = tickets.filter((t) => t.status === 'open' || t.status === 'in_progress').length;

  return (
    <AppPage wide>
      <HeroPanel image={`${API_URL}/media/banners/banner.jpeg`}>
        <p className="text-xs font-semibold uppercase tracking-widest text-primary-300">Support</p>
        <h1 className="mt-3 text-3xl font-semibold tracking-tight text-white sm:text-4xl">How can we help?</h1>
        <p className="mt-2 max-w-xl text-gray-300">Send us a request and we&apos;ll reply by email, usually within 24 hours.</p>
        <div className="mt-6 flex flex-wrap gap-3 text-sm">
          <a href={`mailto:${SUPPORT_EMAIL}`} className="inline-flex items-center gap-2 rounded-lg border border-white/20 bg-white/10 px-3 py-2 text-white backdrop-blur hover:bg-white/20">
            <EnvelopeIcon className="h-4 w-4" />{SUPPORT_EMAIL}
          </a>
          <span className="inline-flex items-center gap-2 rounded-lg border border-white/20 bg-black/20 px-3 py-2 text-gray-200 backdrop-blur">
            <ClockIcon className="h-4 w-4" />Replies within 24 hours
          </span>
          {openCount > 0 && (
            <a href="#tickets" className="inline-flex items-center gap-2 rounded-lg border border-white/20 bg-black/20 px-3 py-2 text-gray-200 backdrop-blur hover:bg-white/10">
              <InboxIcon className="h-4 w-4" />{openCount} open request{openCount > 1 ? 's' : ''}
            </a>
          )}
        </div>
      </HeroPanel>

      <div className="mt-6 grid gap-6 xl:grid-cols-3">
        {/* Request form */}
        <section className={`xl:col-span-2 ${CARD}`}>
          {sent ? (
            <Empty
              icon={CheckCircleIcon}
              title="Request sent"
              action={<button onClick={() => setSent(false)} className={BTN_SECONDARY}>Send another request</button>}
            >
              We&apos;ve got your message and will reply to {form.email || 'your email'} within 24 hours. You can follow it below.
            </Empty>
          ) : (
            <form onSubmit={submit} className="space-y-6 p-5 sm:p-6">
              <div>
                <h2 className="text-lg font-semibold text-slate-900 dark:text-white">New request</h2>
                <p className="text-sm text-slate-500 dark:text-gray-400">What is it about?</p>
              </div>

              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3" role="radiogroup" aria-label="Category">
                {CATEGORIES.map((c) => {
                  const on = form.category === c.value;
                  return (
                    <button
                      type="button"
                      key={c.value}
                      role="radio"
                      aria-checked={on}
                      onClick={() => set('category')(c.value)}
                      className={`flex items-center gap-3 rounded-xl border p-3 text-left text-sm font-medium transition-colors ${on
                        ? 'border-primary-500 bg-primary-50 text-slate-900 dark:bg-primary-600/15 dark:text-white'
                        : 'border-slate-200 text-slate-600 hover:border-slate-300 dark:border-white/10 dark:text-gray-300 dark:hover:border-white/25'}`}
                    >
                      <c.icon className={`h-5 w-5 flex-shrink-0 ${on ? 'text-primary-600 dark:text-primary-300' : 'text-slate-400 dark:text-gray-500'}`} />
                      {c.label}
                    </button>
                  );
                })}
              </div>

              <div className="grid gap-5 sm:grid-cols-[1fr_auto]">
                <Field label="Subject">
                  <input value={form.subject} onChange={set('subject')} required maxLength={200} placeholder="A short summary of the problem" className={INPUT} />
                </Field>
                <div>
                  <span className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-gray-300">Priority</span>
                  <Segmented label="Priority" size="md" value={form.priority} onChange={set('priority')} options={PRIORITIES} />
                </div>
              </div>

              <Field label="Details" hint="Include what you were doing, the subject or question, and any error you saw.">
                <textarea value={form.description} onChange={set('description')} required rows={6} placeholder="Tell us what happened..." className={INPUT} />
              </Field>

              <div className="grid gap-5 sm:grid-cols-2">
                <Field label="Reply to">
                  <input type="email" value={form.email} onChange={set('email')} required autoComplete="email" className={INPUT} />
                </Field>
                <Field label="Phone number">
                  <input
                    type="tel" value={form.phone_number} onChange={set('phone_number')} required
                    placeholder="+962 7X XXX XXXX" autoComplete="tel" maxLength={20}
                    pattern="[+0-9\s\(\)\-]{7,20}" title="Enter a valid phone number (digits, spaces, +, -, parentheses)"
                    className={INPUT}
                  />
                </Field>
              </div>

              {error && <p role="alert" className="rounded-lg bg-rose-500/10 px-4 py-3 text-sm text-rose-700 dark:text-rose-300">{error}</p>}

              <div className="flex justify-end">
                <button type="submit" disabled={sending} className={BTN_PRIMARY}>{sending ? 'Sending...' : 'Send request'}</button>
              </div>
            </form>
          )}
        </section>

        {/* Quick answers */}
        <section className={`self-start ${CARD}`}>
          <div className="border-b border-slate-200 px-5 py-4 dark:border-white/10">
            <h2 className="font-semibold text-slate-900 dark:text-white">Quick answers</h2>
          </div>
          <ul className="divide-y divide-slate-200 dark:divide-white/10">
            {FAQS.map((f, i) => (
              <li key={f.q}>
                <button
                  onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
                  aria-expanded={openFaq === i}
                  className="flex w-full items-center justify-between gap-3 px-5 py-3.5 text-left text-sm font-medium text-slate-900 hover:bg-slate-50 dark:text-white dark:hover:bg-white/[0.03]"
                >
                  {f.q}
                  <span className={`text-lg leading-none text-primary-500 transition-transform ${openFaq === i ? 'rotate-45' : ''}`}>+</span>
                </button>
                {openFaq === i && <p className="px-5 pb-4 text-sm leading-relaxed text-slate-500 dark:text-gray-400">{f.a}</p>}
              </li>
            ))}
          </ul>
          <div className="border-t border-slate-200 px-5 py-4 text-sm dark:border-white/10">
            <Link to="/store" className="inline-flex items-center gap-2 font-medium text-primary-600 hover:underline dark:text-primary-300">
              <AcademicCapIcon className="h-4 w-4" />Plans and pricing
            </Link>
          </div>
        </section>
      </div>

      {/* Tickets */}
      <section id="tickets" className={`mt-6 scroll-mt-20 ${CARD}`}>
        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4 dark:border-white/10">
          <h2 className="font-semibold text-slate-900 dark:text-white">Your requests</h2>
          {tickets.length > 0 && <span className="text-sm text-slate-500 dark:text-gray-400">{tickets.length} total</span>}
        </div>
        {loadingTickets ? (
          <div className="flex h-32 items-center justify-center"><div className="h-6 w-6 animate-spin rounded-full border-b-2 border-primary-600" /></div>
        ) : tickets.length === 0 ? (
          <Empty icon={InboxIcon} title="No requests yet">Requests you send will appear here with their status.</Empty>
        ) : (
          <ul className="divide-y divide-slate-200 dark:divide-white/10">
            {tickets.map((t) => {
              const st = STATUS[t.status] || STATUS.open;
              return (
                <li key={t.id} className="flex flex-col gap-2 px-5 py-4 sm:flex-row sm:items-start sm:justify-between">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="font-medium text-slate-900 dark:text-white">{t.subject}</p>
                      <Pill tone={st.tone}>{st.label}</Pill>
                      {(t.priority === 'high' || t.priority === 'urgent') && <Pill tone="red" className="capitalize">{t.priority}</Pill>}
                    </div>
                    <p className="mt-1 line-clamp-2 text-sm text-slate-500 dark:text-gray-400">{t.description}</p>
                  </div>
                  <div className="flex-shrink-0 text-xs text-slate-500 sm:text-right dark:text-gray-400">
                    <p>{CATEGORY_LABEL[t.category] || 'General'}</p>
                    <p className="mt-0.5">{new Date(t.created_at).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })}</p>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </AppPage>
  );
};

export default Support2;
